import mapboxgl from "mapbox-gl";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BottomSheet } from "react-spring-bottom-sheet";
import "react-spring-bottom-sheet/dist/style.css";
import supabase from "../../services/supabase";
import "../LocalMap/LocalMap.css";

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

const categories = ["Entertainment", "Hidden Gems", "Flowers", "Stargazing", "Hiking"];
const categoryMarkerMap = {
  Entertainment: "/popcornmarker.png",
  "Hidden Gems": "/gemmarker.png",
  Flowers: "/flowermarker.png",
  Stargazing: "/starmarker.png",
  Hiking: "/starmarker.png",
};

const categorySelectedMarkerMap = {
  Entertainment: "/popcornmarker2.png",
  "Hidden Gems": "/gemmarker2.png",
  Flowers: "/flowermarker2.png",
  Stargazing: "/starmarker2.png",
  Hiking: "/starmarker2.png",
};

export default function BayAreaMap() {
  const [map, setMap] = useState(null);
  const [spots, setSpots] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSpotId, setSelectedSpotId] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const markerMapRef = useRef({});
  const mapRef = useRef(null);
  const navigate = useNavigate();

  const bayAreaCenter = [-122.22, 37.77];
  const bayAreaBounds = [
    [-123.6, 36.0],
    [-120.0, 38.7],
  ];

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const mapInstance = new mapboxgl.Map({
      container: mapRef.current,
      style: "mapbox://styles/mapbox/streets-v11",
      center: bayAreaCenter,
      zoom: 8.5,
      minZoom: 1,
      maxBounds: bayAreaBounds,
    });

    setTimeout(() => mapInstance.resize(), 200);
    setMap(mapInstance);

    return () => mapInstance.remove();
  }, []);

  useEffect(() => {
    const fetchBayPlaces = async () => {
      const { data, error } = await supabase.from("bay_places").select("*");
      if (error) {
        console.error("Error fetching Bay Area spots:", error);
      } else {
        setSpots(data);
      }
    };
    fetchBayPlaces();
  }, []);

  const filteredSpots = selectedCategory ? spots.filter((s) => s.category === selectedCategory) : spots;

  useEffect(() => {
    if (!map || !filteredSpots.length) return;

    Object.values(markerMapRef.current).forEach((marker) => marker.remove());
    markerMapRef.current = {};

    filteredSpots.forEach((spot) => {
      const id = spot.id || spot.name;

      const el = document.createElement("img");
      const selected = selectedSpotId === id;
      el.src = selected
        ? categorySelectedMarkerMap[spot.category] || "/marker4.png"
        : categoryMarkerMap[spot.category] || "/newmarker.png";

      el.alt = "Location marker";
      el.style.width = "53px";
      el.style.height = "63px";
      el.style.objectFit = "contain";

      const popup = new mapboxgl.Popup({ offset: 25 }).setHTML(`
        <div>
          <strong>${spot.name}</strong><br />
          <p style="margin: 0.5rem 0; font-size: 0.9rem;">${spot.description || "No description available."}</p>
          <a href="https://www.google.com/maps?q=${spot.lat},${spot.lng}" 
            target="_blank" 
            rel="noopener noreferrer"
            style="display: inline-block; margin-top: 0.5rem; background: #81d1e8; color: white; padding: 0.3rem 0.6rem; border-radius: 4px; text-decoration: none; font-size: 0.85rem;">
            Open in Google Maps
          </a>
        </div>
      `);

      const marker = new mapboxgl.Marker(el)
        .setLngLat([spot.lng, spot.lat])
        .setPopup(popup)
        .addTo(map);

      el.addEventListener("click", () => {
        setSelectedSpotId(null);
        setTimeout(() => {
          map.flyTo({ center: [spot.lng, spot.lat], zoom: 18 });
          popup.addTo(map);
          setSelectedSpotId(id);
        }, 50);
      });

      markerMapRef.current[id] = marker;
    });
  }, [map, filteredSpots, selectedSpotId]);

  const renderSidebarContent = () => (
    <div className="bottom-sheet-content">
        <div className="back-r-button" onClick={() => navigate("/regions")}>
            <div className="back-r-icon">←</div>
            <div className="back-r-label">Back to Regions</div>
        </div>
      {selectedCategory ? (
        <>
          <div className="sidebar-header-desktop">
            <button
              className="back-button"
              onClick={() => {
                setSelectedCategory(null);
                setSelectedSpotId(null);
                map?.flyTo({ center: bayAreaCenter, zoom: 8.5 });
              }}
            >
              ← Back
            </button>
            <h2 className="sidebar-title">{selectedCategory}</h2>
          </div>

          <div className="spot-list">
            {filteredSpots.map((spot) => (
              <div
                key={spot.id}
                className="spot-card"
                onClick={() => {
                  map?.flyTo({ center: [spot.lng, spot.lat], zoom: 14 });
                  const marker = markerMapRef.current[spot.id || spot.name];
                  if (marker) marker.togglePopup();
                }}
              >
                <strong>{spot.name}</strong>
                <p>{spot.description}</p>
              </div>
            ))}
          </div>
        </>
      ) : (
        <>
          <h2 className="sidebar-title">Bay Area Spots</h2>
          <div className="button-card-list">
            {categories.map((cat) => (
              <div
                key={cat}
                className="card-button"
                onClick={() => setSelectedCategory(cat)}
              >
                <div className="card-icon">
                  {cat === "Entertainment"
                    ? "🍿"
                    : cat === "Hidden Gems"
                    ? "💎"
                    : cat === "Flowers"
                    ? "🌸"
                    : cat === "Hiking"
                    ? "⛰️"
                    : "🌠"}
                </div>
                <div className="card-label">{cat}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );

  return (
    <div className="app-container">
      <div className="map-wrapper">
        <div className="map-container" ref={mapRef} />
        {!isMobile ? (
          <div className="styled-sidebar">{renderSidebarContent()}</div>
        ) : (
          <BottomSheet
            open={true}
            defaultSnap={({ maxHeight }) => maxHeight * 0.3}
            snapPoints={({ maxHeight }) => [
              maxHeight * 0.25,
              maxHeight * 0.5,
              maxHeight * 0.9,
            ]}
            blocking={false}
            expandOnContentDrag
            scrollLocking={true}
            header={<div className="bottom-sheet-header" />}
          >
            {renderSidebarContent()}
          </BottomSheet>
        )}
      </div>
    </div>
  );
}
